import React from "react";
import { useHistory } from "react-router-dom";
import { Typography, Button } from "@material-ui/core";
import { useQuestionnaire, usePublishCheck, usePublishQuestionnaire } from "./queries";
import { LOCALES } from "./api";
import { useLocaleDiffPreviews } from "./useLocaleDiffPreviews";
import type { SnapshotRef } from "./useLocaleDiffPreviews";
import DiffShell from "./components/DiffShell";

const ReviewContent: React.VFC<{ id: number }> = ({ id }) => {
  const history = useHistory();

  const questionnaireQuery = useQuestionnaire(id);
  const publishCheckQuery = usePublishCheck(id);
  const publishMutation = usePublishQuestionnaire();

  const conflict = publishCheckQuery.data ?? null;
  const before: SnapshotRef | null = conflict ? { kind: "published", id: conflict.active_published_id } : null;
  const after: SnapshotRef | null = id ? { kind: "draft" } : null;

  const { previewsLoading, localeInfo } = useLocaleDiffPreviews({
    before,
    after,
    draftId: id || null,
    currentLocale: LOCALES[0],
  });

  const allLocalesLoaded = localeInfo.every(l => l.count !== null);
  const totalChanges = localeInfo.reduce((sum, l) => sum + (l.count ?? 0), 0);
  const noChanges = !!before && allLocalesLoaded && totalChanges === 0;

  const handlePublish = () => {
    publishMutation.mutate(id, {
      onSuccess: () => history.push("/health-forms/review"),
    });
  };

  const handleOpenEditor = () => {
    history.push(`/health-forms/questionnaires/${id}`);
  };

  const name = questionnaireQuery.data?.name ?? "...";

  const subtitle = (
    <>
      {conflict && (
        <Typography variant="body2" color="textSecondary">
          Replacing version published {new Date(conflict.active_published_at).toLocaleString()} by{" "}
          {conflict.active_published_by}
        </Typography>
      )}
      {questionnaireQuery.data?.name && (
        <Typography variant="body2">
          <Button
            size="small"
            onClick={() =>
              history.push(`/health-forms/questionnaires/${encodeURIComponent(questionnaireQuery.data!.name!)}/compare`)
            }
          >
            Compare against other versions
          </Button>
        </Typography>
      )}
    </>
  );

  const actions = (
    <>
      <Button size="small" variant="outlined" onClick={handleOpenEditor}>
        Open editor
      </Button>
      <Button
        size="small"
        variant="contained"
        color="primary"
        onClick={handlePublish}
        disabled={publishCheckQuery.isLoading || previewsLoading || publishMutation.isLoading || noChanges}
      >
        {publishMutation.isLoading ? "Publishing..." : "Publish"}
      </Button>
    </>
  );

  return (
    <DiffShell
      embedded
      title={`Review: ${name}`}
      subtitle={subtitle}
      before={before}
      after={after}
      draftId={id || null}
      previousLabel="Currently published"
      nextLabel="About to publish"
      emptyMessage="No prior published version with this name. This will create the first snapshot."
      actions={actions}
      isLoading={publishCheckQuery.isLoading}
      error={publishCheckQuery.error ? "Failed to load publish check" : null}
    />
  );
};

export default ReviewContent;
